import React, { useEffect, useState, useRef } from 'react';
import { planService, userService } from '../services/api';
import { BillingPlan } from '../types';
import { Plus, Edit2, Trash2, Save, X, Settings as SettingsIcon, Users, Key, Upload } from 'lucide-react';

interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  initials: string;
  active: boolean;
}

const Settings: React.FC = () => {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({ planCode: '', planName: '', description: '' });
  const [userFormData, setUserFormData] = useState({ email: '', password: '', firstName: '', lastName: '', initials: '' });
  const [userError, setUserError] = useState('');
  const [activeTab, setActiveTab] = useState<'plans' | 'users'>('plans');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: string[] } | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, usersRes] = await Promise.all([
        planService.getAll(),
        userService.getAll()
      ]);
      setPlans(plansRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await planService.getAll();
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userService.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreate = async () => {
    try {
      await planService.create(formData);
      await fetchPlans();
      setShowNewForm(false);
      setFormData({ planCode: '', planName: '', description: '' });
    } catch (error) {
      console.error('Error creating plan:', error);
    }
  };

  const handleUpdate = async (id: number) => {
    try {
      await planService.update(id, formData);
      await fetchPlans();
      setEditingId(null);
    } catch (error) {
      console.error('Error updating plan:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de eliminar este plan?')) {
      try {
        await planService.delete(id);
        await fetchPlans();
      } catch (error) {
        console.error('Error deleting plan:', error);
      }
    }
  };

  const startEdit = (plan: BillingPlan) => {
    setEditingId(plan.id);
    setFormData({
      planCode: plan.plan_code,
      planName: plan.plan_name,
      description: plan.description || ''
    });
  };

  const handleCreateUser = async () => {
    setUserError('');
    if (!userFormData.email || !userFormData.password || !userFormData.firstName || !userFormData.lastName) {
      setUserError('Todos los campos son requeridos');
      return;
    }
    try {
      await userService.create(userFormData);
      await fetchUsers();
      setShowNewUserForm(false);
      setUserFormData({ email: '', password: '', firstName: '', lastName: '', initials: '' });
    } catch (error: any) {
      setUserError(error.response?.data?.error || 'Error al crear usuario');
    }
  };

  const handleUpdateUser = async (id: number) => {
    setUserError('');
    try {
      const user = users.find(u => u.id === id);
      if (!user) return;
      await userService.update(id, {
        email: userFormData.email,
        firstName: userFormData.firstName,
        lastName: userFormData.lastName,
        initials: userFormData.initials,
        active: user.active
      });
      await fetchUsers();
      setEditingUserId(null);
    } catch (error: any) {
      setUserError(error.response?.data?.error || 'Error al actualizar usuario');
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (window.confirm('¿Está seguro de desactivar este usuario?')) {
      try {
        await userService.delete(id);
        await fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setUserError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      await userService.updatePassword(showPasswordModal!, newPassword);
      setShowPasswordModal(null);
      setNewPassword('');
      alert('Contraseña actualizada exitosamente');
    } catch (error: any) {
      setUserError(error.response?.data?.error || 'Error al cambiar contraseña');
    }
  };

  const startEditUser = (user: AdminUser) => {
    setEditingUserId(user.id);
    setUserFormData({
      email: user.email,
      password: '',
      firstName: user.first_name,
      lastName: user.last_name,
      initials: user.initials || ''
    });
    setUserError('');
  };

  const handleImportCSV = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);

    try {
      const text = await importFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      // Skip header if exists
      const dataLines = lines[0].toLowerCase().includes('codigo') || lines[0].toLowerCase().includes('code') 
        ? lines.slice(1) 
        : lines;

      const plans = dataLines.map(line => {
        const parts = line.split(',').map(p => p.trim().replace(/^"|"$/g, ''));
        return {
          planCode: parts[0] || '',
          planName: parts[1] || '',
          description: parts[2] || ''
        };
      }).filter(p => p.planCode && p.planName);

      const response = await planService.import(plans);
      setImportResult(response.data);
      await fetchPlans();
    } catch (error: any) {
      setImportResult({ imported: 0, errors: [error.response?.data?.error || 'Error al importar'] });
    } finally {
      setImporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <SettingsIcon className="text-gray-600" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Configuración</h1>
          <p className="text-gray-500">Administrar sistema</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('plans')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                activeTab === 'plans'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Planes de Facturación
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                activeTab === 'users'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users size={18} />
              Facturadoras / Empleados
            </button>
          </nav>
        </div>

        {/* Plans Tab Content */}
        {activeTab === 'plans' && (
          <>
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Planes de Facturación</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowImportModal(true); setImportFile(null); setImportResult(null); }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
                >
                  <Upload size={18} />
                  Importar CSV
                </button>
                <button
                  onClick={() => setShowNewForm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                  <Plus size={18} />
                  Nuevo Plan
                </button>
              </div>
            </div>

        {showNewForm && (
          <div className="p-4 bg-blue-50 border-b border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Código"
                value={formData.planCode}
                onChange={(e) => setFormData({ ...formData, planCode: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Nombre"
                value={formData.planName}
                onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Descripción"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
                >
                  <Save size={18} />
                  Guardar
                </button>
                <button
                  onClick={() => {
                    setShowNewForm(false);
                    setFormData({ planCode: '', planName: '', description: '' });
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {plans.map((plan) => (
                <tr key={plan.id} className="hover:bg-gray-50">
                  {editingId === plan.id ? (
                    <>
                      <td className="px-6 py-4">
                        <input
                          type="text"
                          value={formData.planCode}
                          onChange={(e) => setFormData({ ...formData, planCode: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="text"
                          value={formData.planName}
                          onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="text"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleUpdate(plan.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          >
                            <Save size={18} />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4 font-medium text-blue-600">{plan.plan_code}</td>
                      <td className="px-6 py-4 text-gray-700">{plan.plan_name}</td>
                      <td className="px-6 py-4 text-gray-500">{plan.description || '-'}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => startEdit(plan)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(plan.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {plans.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    No hay planes registrados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
          </>
        )}

        {/* Users Tab Content */}
        {activeTab === 'users' && (
          <>
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Facturadoras / Empleados iBilling</h2>
              <button
                onClick={() => { setShowNewUserForm(true); setUserError(''); }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
              >
                <Plus size={18} />
                Nuevo Usuario
              </button>
            </div>

        {userError && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {userError}
          </div>
        )}

        {showNewUserForm && (
          <div className="p-4 bg-blue-50 border-b border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <input
                type="text"
                placeholder="Nombre *"
                value={userFormData.firstName}
                onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Apellido *"
                value={userFormData.lastName}
                onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Email *"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                placeholder="Contraseña *"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Iniciales"
                value={userFormData.initials}
                onChange={(e) => setUserFormData({ ...userFormData, initials: e.target.value })}
                maxLength={5}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreateUser}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
                >
                  <Save size={18} />
                  Guardar
                </button>
                <button
                  onClick={() => {
                    setShowNewUserForm(false);
                    setUserFormData({ email: '', password: '', firstName: '', lastName: '', initials: '' });
                    setUserError('');
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Iniciales</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  {editingUserId === user.id ? (
                    <>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={userFormData.firstName}
                            onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                            className="w-24 px-2 py-1 border border-gray-300 rounded"
                            placeholder="Nombre"
                          />
                          <input
                            type="text"
                            value={userFormData.lastName}
                            onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                            className="w-24 px-2 py-1 border border-gray-300 rounded"
                            placeholder="Apellido"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="email"
                          value={userFormData.email}
                          onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="text"
                          value={userFormData.initials}
                          onChange={(e) => setUserFormData({ ...userFormData, initials: e.target.value })}
                          className="w-16 px-2 py-1 border border-gray-300 rounded"
                          maxLength={5}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${user.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {user.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleUpdateUser(user.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          >
                            <Save size={18} />
                          </button>
                          <button
                            onClick={() => setEditingUserId(null)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4 font-medium text-gray-800">{user.first_name} {user.last_name}</td>
                      <td className="px-6 py-4 text-gray-600">{user.email}</td>
                      <td className="px-6 py-4 text-gray-600">{user.initials || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${user.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {user.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => startEditUser(user)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Editar"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => { setShowPasswordModal(user.id); setNewPassword(''); setUserError(''); }}
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                            title="Cambiar contraseña"
                          >
                            <Key size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Desactivar"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No hay usuarios registrados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
          </>
        )}

      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Cambiar Contraseña</h3>
            {userError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {userError}
              </div>
            )}
            <input
              type="password"
              placeholder="Nueva contraseña (mín. 6 caracteres)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowPasswordModal(null); setNewPassword(''); setUserError(''); }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleChangePassword}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Plans Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Importar Planes de Facturación</h3>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
              <p className="font-medium mb-2">Formato del CSV:</p>
              <code className="block bg-gray-200 p-2 rounded text-xs">
                Codigo,Nombre,Descripcion<br/>
                MCS,MCS Clásico,Plan MCS Clásico<br/>
                MMM,MMM Vital,Plan MMM Vital
              </code>
            </div>

            <input
              ref={importInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
            />

            {importResult && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${importResult.errors.length > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                <p className="font-medium text-green-700">Planes importados: {importResult.imported}</p>
                {importResult.errors.length > 0 && (
                  <div className="mt-2 text-red-600">
                    <p className="font-medium">Errores:</p>
                    <ul className="list-disc list-inside text-xs">
                      {importResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowImportModal(false); setImportFile(null); setImportResult(null); }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
              >
                Cerrar
              </button>
              <button
                onClick={handleImportCSV}
                disabled={!importFile || importing}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
              >
                {importing ? 'Importando...' : 'Importar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
